import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import tw from 'tailwind.macro';
import Accordion from '../Accordion';
import { CustomH2, CustomH3, HTMLText } from '../SEO';
import { manualDataPropTypes } from './constants';

const Text = styled(HTMLText)`
  ${tw`my-def`};
  line-height: initial;
`;

const CustomH2MarginTop = styled(CustomH2)`
  ${tw`mt-abs8`};
`;

/**
 @description Wraps an element with the CustomH2 component
 @param {React.Element} element - The element to be wrapped in a CustomH2 component
 @returns {JSX.Element} The wrapped element in a CustomH2 component
 */
const handleH2 = (element, isFirstElement) => {
  return isFirstElement ? (
    <CustomH2>{element.props.children}</CustomH2>
  ) : (
    <CustomH2MarginTop>{element.props.children}</CustomH2MarginTop>
  );
};

/**
 @description Wraps the content between H3 & H2 elements with an Accordion component
 @param {React.Element[]} content - An array of elements to be processed
 @param {number} i - The index of the current H3 element in the content array
 @param {boolean} [open=false] - Indicates if the accordion should be open by default
 @returns {{accordionElement: JSX.Element, newIndex: number}} - An object containing the Accordion-wrapped element and the updated index
 */
const handleH3 = (content, i, open) => {
  let toggleContent = [];
  let j;

  for (j = i + 1; j < content.length; ++j) {
    if (content[j].type !== 'h3' && content[j].type !== 'h2') {
      toggleContent.push(content[j]);
    } else {
      break;
    }
  }

  const accordionElement = (
    <Accordion
      content={<Text>{toggleContent}</Text>}
      header={<CustomH3>{content[i].props.children}</CustomH3>}
      id={`${content[i].props.children}`}
      open={open || false}
    />
  );

  return { accordionElement, newIndex: j - 1 };
};

const ManualDataSection = ({ manualData, open }) => {
  const content = parse(manualData.content);
  const result = [];

  for (let i = 0; i < content.length; ++i) {
    if (content[i].type) {
      if (content[i].type === 'h2') {
        result.push(handleH2(content[i], result.length === 0));
      } else if (content[i].type === 'h3') {
        const { accordionElement, newIndex } = handleH3(content, i, open);
        result.push(accordionElement);
        i = newIndex;
      } else {
        result.push(content[i]);
      }
    }
  }

  return <Text key={manualData.id}>{result}</Text>;
};

ManualDataSection.propTypes = {
  manualData: manualDataPropTypes,
  open: PropTypes.bool,
};

export { ManualDataSection };
